// The device token exchange (spec/07 §5, spec/10 §3.3; F-STN-05, #83). How a paired PC proves it is
// itself: it signs a server challenge with its Ed25519 device key and gets short-lived tokens for the
// api and the realtime gateway. Pairing (F-STN-02) binds the key to a station; this is what the bound
// key then does.
//
//   POST /v1/device-auth/challenge   { deviceId } → { nonce, expiresAt }         nonce valid 60 s
//   POST /v1/device-auth/token       the signature over (nonce, deviceId, timestamp) → the tokens
//
// Both are public — the device has no session, it *is* the credential — and rate-limited per device
// (1200 requests per minute, bursts allowed for a reconnect). Every failure is the same `401`: an
// unknown device, a bad signature, a spent or expired nonce and a revoked device are one answer, so
// the endpoint is no oracle. The device's response to `401` is always the same — get a fresh
// challenge and retry — and, if it stays refused, it has been unpaired or revoked and the gateway has
// already closed its socket (`4403`, spec/09 §9).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { DeviceIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse } from "../openapi/responses.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
// ---------------------------------------------------------------------------------------------
// POST /v1/device-auth/challenge
// ---------------------------------------------------------------------------------------------
export const DeviceAuthChallengeRequestSchema = z
    .object({
    deviceId: DeviceIdSchema,
})
    .openapi('DeviceAuthChallengeRequest', { description: 'Which device is asking for a challenge.' });
export const DeviceAuthChallengeSchema = z
    .object({
    /** The nonce to sign, base64url. Single-use, valid 60 s (spec/07 §5). */
    nonce: z.string().min(1).max(128),
    expiresAt: IsoDateTimeSchema,
})
    .openapi('DeviceAuthChallenge', { description: 'A nonce to sign with the device key, good for 60 seconds.' });
export const createDeviceAuthChallengeRoute = createRoute({
    method: 'post',
    path: '/v1/device-auth/challenge',
    operationId: 'createDeviceAuthChallenge',
    tags: ['stations'],
    summary: 'Start the device token exchange: get a nonce to sign',
    description: 'Public and rate-limited per device (spec/07 §5). Returns a single-use nonce valid 60 seconds. The device ' +
        'signs `(nonce, deviceId, timestamp)` with its Ed25519 key and presents the signature to ' +
        '`POST /v1/device-auth/token`. An unknown device gets a nonce all the same — the endpoint is no oracle.',
    request: { body: { content: { 'application/json': { schema: DeviceAuthChallengeRequestSchema } }, required: true } },
    responses: {
        200: jsonResponse(DeviceAuthChallengeSchema, 'The challenge nonce.'),
        400: commonProblemResponses[400],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/device-auth/token
// ---------------------------------------------------------------------------------------------
export const DeviceAuthTokenRequestSchema = z
    .object({
    deviceId: DeviceIdSchema,
    /** The nonce from the challenge (spec/07 §5). */
    nonce: z.string().min(1).max(128),
    /** The client's timestamp, part of the signed message; must be recent (spec/07 §5). */
    timestamp: IsoDateTimeSchema,
    /** The Ed25519 signature over `(nonce, deviceId, timestamp)`, base64url. */
    signature: z.string().min(1).max(512),
})
    .openapi('DeviceAuthTokenRequest', {
    description: 'The signed challenge: the Ed25519 signature over `(nonce, deviceId, timestamp)`.',
});
export const DeviceAuthTokenSchema = z
    .object({
    /**
     * The device credential: an EdDSA JWT, `aud: ["api","realtime"]`, `sub: dev_…`, 15 minutes
     * (spec/07 §5). Sent as `Authorization: Bearer` to the api. It also authenticates the gateway
     * directly, so a reconnect storm needs no token request per station.
     */
    deviceToken: z.string().min(1),
    /**
     * The realtime token for the gateway's first frame (`auth.connect`), `aud: realtime`, 15 minutes.
     * Read as the station principal (spec/09 §1). Never goes in a URL.
     */
    realtimeToken: z.string().min(1),
    /** When both tokens expire; refresh before then (spec/07 §5). */
    expiresAt: IsoDateTimeSchema,
})
    .openapi('DeviceAuthToken', { description: 'The device and realtime tokens, 15 minutes each (spec/07 §5).' });
export const createDeviceAuthTokenRoute = createRoute({
    method: 'post',
    path: '/v1/device-auth/token',
    operationId: 'createDeviceAuthToken',
    tags: ['stations'],
    summary: 'Exchange a signed challenge for the device and realtime tokens',
    description: 'Public and rate-limited per device (spec/07 §5). Verifies the Ed25519 signature over ' +
        '`(nonce, deviceId, timestamp)` against the device\'s bound public key, spends the nonce, and returns a ' +
        'device token and a realtime token, 15 minutes each. Every refusal — unknown device, bad signature, spent or ' +
        'expired nonce, revoked device — is one `401`, so the endpoint tells an attacker nothing.',
    request: { body: { content: { 'application/json': { schema: DeviceAuthTokenRequestSchema } }, required: true } },
    responses: {
        200: jsonResponse(DeviceAuthTokenSchema, 'The device and realtime tokens.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const DEVICE_AUTH_ROUTES = [createDeviceAuthChallengeRoute, createDeviceAuthTokenRoute];
//# sourceMappingURL=device-auth.js.map